import { useTranslation } from "react-i18next";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { Table, TableCell, TableHead, TableRow, TableBody, TableFooter } from '@mui/material';
import { PoolXMoney } from "../models/money";
import { AccountingAmount, DetailedRound, Group } from "../models/round";
import { CouponShare } from "../models/couponshares";
import { Group as SyndicateGroup } from "../models/syndicate";

interface ResultRowProps {
  index: number;
  maxNum: number;
  result: SyndicateGroup | undefined;
  payoutPerRow: AccountingAmount;
  rows: string;
  shareOfCoupon: number;
  isRightToLeft: boolean;
  isCashedOut?: boolean;
}

function ResultRow({
  index,
  maxNum,
  result,
  payoutPerRow,
  rows,
  shareOfCoupon,
  isRightToLeft,
  isCashedOut
}: ResultRowProps) {
  const { t } = useTranslation();

  const numCorrect = maxNum - index;
  if (!result) {
    return <div></div>
  }
  const money = payoutPerRow.money || {} as PoolXMoney;
  if (!money) {
    return <div></div>
  }
  let actualPayout = Number(money.amount);
  const numOwn = result.rows;

  const totalPayoutCurrency = money.currency ? money.currency : 'SEK';

  // We cannot use the amount in result.payout as one row is not always exactly equal to one stake unit, this depends on
  // the operator currency
  const ownPayout = numOwn * actualPayout * shareOfCoupon;

  const rowSx = {
    fontSize: '11px',
    padding: '5px 14px 5px 14px',
    fontFamily: 'var(--font-body)',
    color: index % 2 !== 0 ? 'var(--text-black)' : 'var(--text-black-dark-background)'
  }

  const alternating = {
    '&:nth-of-type(odd)': {
      backgroundColor: 'var(--background_1)'
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'var(--background_2)'
    },
  }

  let whole = false;
  if (totalPayoutCurrency === 'SEK') {
    whole = true;
  }

  return (

    isRightToLeft ? (
      <TableRow sx={alternating}>
        <TableCell align='right' sx={rowSx}>{isCashedOut ? '' : formatStringCurrency(ownPayout, totalPayoutCurrency, t, whole)}</TableCell>
        <TableCell align='right' sx={rowSx}>{isCashedOut ? '' : `${numOwn} ${t('st')}`}</TableCell>
        <TableCell sx={rowSx}>{formatStringCurrency(actualPayout, totalPayoutCurrency, t, whole)}</TableCell>
        <TableCell align='right' sx={rowSx}>{rows ? (Number(rows) < 1 ? Math.ceil(Number(rows)) : Math.round(Number(rows))) : 'NA'} {t('st')}</TableCell>
        <TableCell align='right' sx={rowSx}>{numCorrect}</TableCell>
      </TableRow>
    ) : (
      <TableRow sx={alternating}>
        <TableCell sx={rowSx}>{numCorrect}</TableCell>
        <TableCell sx={rowSx}>{rows ? (Number(rows) < 1 ? Math.ceil(Number(rows)) : Math.round(Number(rows))) : 'NA'} {t('st')}</TableCell>
        <TableCell align='right' sx={rowSx}>{formatStringCurrency(actualPayout, totalPayoutCurrency, t, whole)}</TableCell>
        <TableCell sx={rowSx}>{isCashedOut ? '' : `${numOwn} ${t('st')}`}</TableCell>
        <TableCell align='right' sx={rowSx}>{isCashedOut ? '' : formatStringCurrency(ownPayout, totalPayoutCurrency, t, whole)}</TableCell>
      </TableRow>
    )
  );
}

interface WinInfoProps {
  round: DetailedRound;
  couponShare: CouponShare;
  isRightToLeft: boolean;
}

export default function WinInfo({ round, couponShare, isRightToLeft }: WinInfoProps) {
  const { fixtures = [] } = round || {};
  const { t } = useTranslation();

  if (!couponShare) {
    return <></>;
  }

  const win: PoolXMoney = couponShare.operatorResult ?? couponShare.operatorPreliminaryResult ?? { amount: 0, currency: 'SEK' };
  const resultGroups = couponShare.coupon?.preliminaryResult?.groups ?? [];
  const groups: Group[] = round.operatorPrizePlan?.payoutGroups ?? [];

  const headerSx = {
    fontSize: '11px',
    fontWeight: 'bold',
    color: 'var(--text-black)',
    padding: '5px 14px 5px 14px',
    fontFamily: 'var(--font-body)'
  }

  const footerSx = {
    fontSize: '11px',
    fontWeight: 'bold',
    padding: '5px 14px 5px 14px',
    color: 'var(--text-black)',
    backgroundColor: 'var(--background_2)',
    fontFamily: 'var(--font-body)'
  }

  let whole = false;
  if (win.currency === 'SEK') {
    whole = true;
  }

  return groups && groups.length > 0 ? (

    <Table>
      <TableHead sx={
        {
          background: 'var(--background_2)',
        }
      }>
        {isRightToLeft ? (
          <TableRow>
            <TableCell sx={headerSx}>{t('profit')}</TableCell>
            <TableCell align='right' sx={headerSx}>{t('your_rows')}</TableCell>
            <TableCell sx={headerSx}>{t('prize')}</TableCell>
            <TableCell align='right' sx={headerSx}>{t('winners')}</TableCell>
            <TableCell align='right' sx={headerSx}>{t('correct_rows')}</TableCell>
          </TableRow>
        ) : (
          <TableRow>
            <TableCell sx={headerSx}>{t('correct_rows')}</TableCell>
            <TableCell sx={headerSx}>{t('winners')}</TableCell>
            <TableCell align='right' sx={headerSx}>{t('prize')}</TableCell>
            <TableCell sx={headerSx}>{t('your_rows')}</TableCell>
            <TableCell align='right' sx={headerSx}>{t('profit')}</TableCell>
          </TableRow>
        )}
      </TableHead>

      <TableBody sx={
        {
          background: 'var(--off-black)',
          color: 'var(--text-white)',
          fontSize: '11px',
          fontWeight: 'bold'
        }
      }>
        {groups && groups.map((group, i) => (
          <ResultRow
            key={`${i} result row`}
            maxNum={fixtures.length}
            result={resultGroups[i]}
            index={i}
            shareOfCoupon={Number(couponShare.totalStakePercentage)}
            isRightToLeft={isRightToLeft}
            isCashedOut={couponShare.status === 'CASHED_OUT'}
            {...group}
          />
        ))}
      </TableBody>

      {isRightToLeft ? (
        <TableFooter>
          <TableRow>
            <TableCell sx={footerSx}>{formatStringCurrency(win.amount, win.currency, t, whole)}</TableCell>
            <TableCell align='right' sx={footerSx} />
            <TableCell align='right' sx={footerSx} />
            <TableCell align='right' sx={footerSx} />
            <TableCell align='right' sx={footerSx}>{t('total')}:</TableCell>
          </TableRow>
        </TableFooter>
      ) : (
        <TableFooter>
          <TableRow>
            <TableCell sx={footerSx}>{t('total')}:</TableCell>
            <TableCell sx={footerSx} />
            <TableCell sx={footerSx} />
            <TableCell sx={footerSx} />
            <TableCell align='right' sx={footerSx}>{formatStringCurrency(win.amount, win.currency, t, whole)}</TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>

  ) : <></>;
}
